import { useCallback, useEffect, useRef, useState } from 'react';
import { getProjectServices } from '../services/SandboxService';

/**
 * The run state of many projects (the Projects list), loaded lazily: a project is asked for
 * (`GET /api/v1/projects/:id/services`, from the pod it runs in) only once its row is visible (`want`), a few at a
 * time (`concurrency`), and the visible ones again every `intervalMs` while the page is visible, or on `refreshAll`
 * (the list's own refresh). Returns `{ states, want, unwant, refresh, refreshAll }`; `states` maps a project id to its
 * answer (null when vfs-service has no such route) once loaded.
 */
export default function useProjectsRunState({ enabled = true, concurrency = 3, intervalMs = 15000 } = {}) {
  const [states, setStates] = useState({});
  const visible = useRef(new Set());
  const queue = useRef([]);
  const queued = useRef(new Set());
  const inFlight = useRef(0);
  const live = useRef(true);
  // Projects vfs-service answered 404 for (`getProjectServices` null): the project is not there (deleted, or listed
  // by mistake) or the route is unknown. Asking again every `intervalMs` would only repeat the 404, so the periodic
  // refresh skips them; an explicit `refresh(projectId)` or `refreshAll` still asks.
  const missing = useRef(new Set());

  const pump = useCallback(() => {
    while (live.current && inFlight.current < concurrency && queue.current.length) {
      const projectId = queue.current.shift();
      queued.current.delete(projectId);
      inFlight.current += 1;
      getProjectServices(projectId)
        .then((answer) => {
          if (answer === null) { missing.current.add(projectId); } else { missing.current.delete(projectId); }
          if (live.current) { setStates((prev) => ({ ...prev, [projectId]: answer })); }
        })
        .catch(() => { /* a blip: the last answer stays */ })
        .finally(() => {
          inFlight.current -= 1;
          pump();
        });
    }
  }, [concurrency]);

  /** Asks for a project's state (again), behind the ones already waiting. */
  const refresh = useCallback((projectId) => {
    if (!enabled || !projectId || queued.current.has(projectId)) { return; }
    queued.current.add(projectId);
    queue.current.push(projectId);
    pump();
  }, [enabled, pump]);

  /** A row came into view: its project is loaded if it is not yet, and kept fresh while it stays visible. */
  const want = useCallback((projectId) => {
    if (visible.current.has(projectId)) { return; }
    visible.current.add(projectId);
    setStates((prev) => {
      if (!(projectId in prev)) { refresh(projectId); }
      return prev;
    });
  }, [refresh]);

  const unwant = useCallback((projectId) => { visible.current.delete(projectId); }, []);

  const refreshAll = useCallback(() => { visible.current.forEach((projectId) => refresh(projectId)); }, [refresh]);

  useEffect(() => {
    live.current = true;
    if (!enabled) { return () => { live.current = false; }; }
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') { return; }
      visible.current.forEach((projectId) => { if (!missing.current.has(projectId)) { refresh(projectId); } });
    }, intervalMs);
    return () => {
      live.current = false;
      clearInterval(timer);
    };
  }, [enabled, intervalMs, refresh]);

  return { states, want, unwant, refresh, refreshAll };
}
